import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  CalendarDays,
  Check,
  Clipboard,
  CreditCard,
  Database,
  MonitorSmartphone,
  Shield,
  Sparkles,
  Target,
} from "lucide-react";
import { fetchUserDetail } from "@/lib/queries";
import { ReviewChart } from "@/components/Charts";
import { ago, date, dateTime, number, percent, titleCase } from "@/lib/format";
import type { DailyMetric, UserDetail } from "@/lib/types";


function Consent({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <span className={`badge ${enabled ? "badge-mint" : ""}`}>
      {enabled ? <Check size={11} /> : null}
      {label}
    </span>
  );
}

function Meta({ k, v }: { k: string; v: string }) {
  return (
    <div className="meta-tile">
      <div className="k">{k}</div>
      <div className="v">{v}</div>
    </div>
  );
}

function rollupsAsMetrics(detail: UserDetail): DailyMetric[] {
  return detail.rollups.map((row) => ({
    day: row.day,
    dau: 0,
    new_users: 0,
    reviews: row.reviews,
    sessions: row.sessions,
    ai_imports: row.ai_imports,
    pro_active: 0,
    mrr_cents: 0,
  }));
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const detail = useQuery({
    queryKey: ["user", id],
    queryFn: () => fetchUserDetail(id!),
    enabled: Boolean(id),
  });

  if (detail.isLoading) {
    return (
      <div className="loading-block">
        <div className="spinner" />
        Loading learner…
      </div>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <div className="stack">
        <Link to="/users" className="btn btn-quiet btn-sm" style={{ width: "fit-content" }}>
          <ArrowLeft size={13} /> Back to users
        </Link>
        <div className="banner banner-danger">
          Could not load this user: {(detail.error as Error | null)?.message ?? "not found"}
        </div>
      </div>
    );
  }

  const data = detail.data;
  const p = data.profile;
  const reviews = data.rollups.reduce((total, row) => total + row.reviews, 0);
  const minutes = Math.round(data.rollups.reduce((total, row) => total + row.study_ms, 0) / 60000);
  const activeDays = data.rollups.filter((row) => row.reviews > 0).length;
  const correct = data.reviews.filter((row) => row.rating >= 3).length;
  const accuracy = data.reviews.length ? correct / data.reviews.length : 0;

  async function copyId() {
    await navigator.clipboard.writeText(p.id);
  }

  return (
    <>
      <div className="row-between">
        <Link to="/users" className="btn btn-quiet btn-sm">
          <ArrowLeft size={13} /> Back to users
        </Link>
        <button className="btn btn-ghost btn-sm" onClick={() => void copyId()}>
          <Clipboard size={13} />
          Copy user id
        </button>
      </div>

      {p.deleted_at && (
        <div className="banner banner-danger">
          <Shield size={16} />
          Account scheduled for deletion on {dateTime(p.deletion_scheduled_for)}. Data is
          still readable until the purge job runs.
        </div>
      )}

      <section className="card user-hero">
        <div className="avatar">{p.display_name.slice(0, 1).toUpperCase()}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="row" style={{ flexWrap: "wrap" }}>
            <h1>{p.display_name}</h1>
            <span className={`badge ${p.plan === "pro" ? "badge-coral" : ""}`}>{p.plan}</span>
            {p.onboarding_completed ? (
              <span className="badge badge-mint">onboarded</span>
            ) : (
              <span className="badge badge-sunshine">onboarding</span>
            )}
            {p.trial_ends_at && <span className="badge badge-grape">trial</span>}
          </div>
          <div className="small faint mono" style={{ marginTop: 3 }}>{p.id}</div>
        </div>
        <div className="right small muted">
          <div>Joined {date(p.created_at)}</div>
          <div>Level {p.level} · {number(p.xp_total)} XP</div>
        </div>
      </section>

      <div className="meta-grid">
        <Meta k="Current streak" v={`${p.streak_current} days`} />
        <Meta k="Best streak" v={`${p.streak_best} days`} />
        <Meta k="Study goal" v={`${p.study_goal} cards/day`} />
        <Meta k="Timezone" v={p.timezone} />
        <Meta k="Locale" v={p.locale} />
        <Meta k="Country" v={p.country_code ?? "—"} />
        <Meta k="Plan expires" v={p.plan_expires_at ? date(p.plan_expires_at) : "—"} />
        <Meta k="Trial ends" v={p.trial_ends_at ? date(p.trial_ends_at) : "—"} />
      </div>

      <div className="grid grid-2">
        <section className="card">
          <div className="card-head">
            <div>
              <h2>Study · 30 days</h2>
              <p>{activeDays} active days · {minutes} minutes</p>
            </div>
            <div className="spacer" />
            <span className="badge badge-mint">
              <Target size={12} />
              {percent(accuracy, 0)} recall
            </span>
          </div>
          <div className="card-pad">
            <ReviewChart data={rollupsAsMetrics(data)} />
            <div className="grid grid-3" style={{ marginTop: 10 }}>
              <div>
                <div className="kpi-label">Reviews</div>
                <div className="kpi-value" style={{ fontSize: 22 }}>{number(reviews)}</div>
              </div>
              <div>
                <div className="kpi-label">Sessions</div>
                <div className="kpi-value" style={{ fontSize: 22 }}>
                  {number(data.rollups.reduce((total, row) => total + row.sessions, 0))}
                </div>
              </div>
              <div>
                <div className="kpi-label">AI imports</div>
                <div className="kpi-value" style={{ fontSize: 22 }}>
                  {number(data.rollups.reduce((total, row) => total + row.ai_imports, 0))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="card">
          <div className="card-head">
            <div>
              <h2>Money & consent</h2>
              <p>Subscription state and privacy choices</p>
            </div>
          </div>
          <div className="card-pad">
            {data.subscription ? (
              <div className="stack" style={{ gap: 0 }}>
                <div className="stat-line">
                  <span className="label">Status</span>
                  <span className="value">{String(data.subscription.status ?? "—")}</span>
                </div>
                <div className="stat-line">
                  <span className="label">Product</span>
                  <span className="value mono">{String(data.subscription.product_id ?? "—")}</span>
                </div>
                <div className="stat-line">
                  <span className="label">Billing</span>
                  <span className="value">{String(data.subscription.billing_period ?? "—")}</span>
                </div>
                <div className="stat-line">
                  <span className="label">Paid through</span>
                  <span className="value">
                    {data.subscription.expires_at
                      ? date(String(data.subscription.expires_at))
                      : "—"}
                  </span>
                </div>
                <div className="stat-line">
                  <span className="label">Renews</span>
                  <span className="value">{data.subscription.will_renew ? "yes" : "no"}</span>
                </div>
              </div>
            ) : (
              <div className="small muted">No subscription record. Free plan.</div>
            )}

            <div className="divider" />
            <div className="kpi-label" style={{ marginBottom: 8 }}>Consent</div>
            <div className="pill-row">
              <Consent label="analytics" enabled={p.consent_analytics} />
              <Consent label="AI" enabled={p.consent_ai_processing} />
              <Consent label="personalization" enabled={p.consent_personalization} />
              <Consent label="marketing" enabled={p.consent_marketing} />
            </div>
            <div className="divider" />
            <div className="kpi-label" style={{ marginBottom: 8 }}>Notification prefs</div>
            {data.prefs ? (
              <div className="small muted">
                {String(data.prefs.max_per_day ?? 2)}/day · quiet {String(data.prefs.quiet_start ?? "22:00")}–
                {String(data.prefs.quiet_end ?? "08:00")} · leagues{" "}
                {data.prefs.leagues ? "on" : "off"} · offers {data.prefs.offers ? "on" : "off"}
              </div>
            ) : (
              <div className="small muted">No preferences row.</div>
            )}
          </div>
        </section>
      </div>

      <div className="grid grid-2">
        <section className="card">
          <div className="card-head">
            <div>
              <h2>Recent activity</h2>
              <p>Last 50 events</p>
            </div>
            <div className="spacer" />
            <span className="badge badge-sky">
              <Sparkles size={12} />
              {data.events.length}
            </span>
          </div>
          <div className="card-pad timeline">
            {data.events.length === 0 && <div className="empty small">No events yet.</div>}
            {data.events.map((event) => (
              <div className="timeline-item" key={event.id}>
                <div className="timeline-dot" />
                <div>
                  <div className="timeline-title">{titleCase(event.name)}</div>
                  <div className="timeline-meta">
                    {dateTime(event.occurred_at)} · {event.app_version ?? "—"} · {event.platform ?? "—"}
                  </div>
                  {Object.keys(event.properties ?? {}).length > 0 && (
                    <div className="mono faint" style={{ marginTop: 3, wordBreak: "break-word" }}>
                      {JSON.stringify(event.properties)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="card">
          <div className="card-head">
            <div>
              <h2>Recent reviews</h2>
              <p>Latest 20 ratings</p>
            </div>
            <div className="spacer" />
            <span className="badge badge-sunshine">
              <CalendarDays size={12} />
              {data.reviews.length}
            </span>
          </div>
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Rating</th>
                  <th>Duration</th>
                  <th>Deck</th>
                </tr>
              </thead>
              <tbody>
                {data.reviews.length === 0 && (
                  <tr><td colSpan={4} className="empty">No reviews yet.</td></tr>
                )}
                {data.reviews.map((review) => (
                  <tr key={review.id}>
                    <td className="nowrap faint">{ago(review.reviewed_at)}</td>
                    <td>
                      <span className={`badge ${review.rating === 1 ? "badge-rose" : review.rating === 2 ? "badge-sunshine" : "badge-mint"}`}>
                        {["again", "hard", "good", "easy"][review.rating - 1] ?? review.rating}
                      </span>
                    </td>
                    <td>{review.duration_ms ? `${Math.round(review.duration_ms / 1000)}s` : "—"}</td>
                    <td className="mono faint">{review.deck_id.slice(0, 8)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div className="grid grid-3">
        <section className="card card-pad">
          <div className="kpi-label" style={{ marginBottom: 8 }}>
            <MonitorSmartphone size={12} style={{ verticalAlign: -2 }} /> Devices
          </div>
          {data.devices.length === 0 && <div className="small muted">No device tokens.</div>}
          {data.devices.map((device) => (
            <div className="stat-line" key={device.id}>
              <span className="label">{device.platform} · {device.app_version ?? "—"}</span>
              <span className="value small">{ago(device.last_seen_at)}</span>
            </div>
          ))}
        </section>

        <section className="card card-pad">
          <div className="kpi-label" style={{ marginBottom: 8 }}>
            <Database size={12} style={{ verticalAlign: -2 }} /> AI usage
          </div>
          {data.ai_usage.length === 0 && <div className="small muted">No AI imports.</div>}
          {data.ai_usage.map((usage) => (
            <div className="stat-line" key={usage.day}>
              <span className="label">{date(usage.day)}</span>
              <span className="value">{usage.imports_used} used · {usage.rewarded_credits} bonus</span>
            </div>
          ))}
        </section>

        <section className="card card-pad">
          <div className="kpi-label" style={{ marginBottom: 8 }}>
            <CreditCard size={12} style={{ verticalAlign: -2 }} /> Notifications
          </div>
          {data.notifications.length === 0 && <div className="small muted">None sent.</div>}
          {data.notifications.slice(0, 6).map((notification) => (
            <div className="stat-line" key={notification.id}>
              <span className="label">{titleCase(notification.category)}</span>
              <span className="value small">{notification.opened_at ? "opened" : "sent"}</span>
            </div>
          ))}
        </section>
      </div>
    </>
  );
}
